/**
 * Ask Swifty about the open document.
 *
 * Answers stream in as Swifty writes them. Page citations ("[p. 4]") are
 * links to the page, so every claim can be checked where it came from.
 */

import { useEffect, useRef, useState } from 'react'
import { api } from '../api'

interface Message {
  role: 'user' | 'assistant'
  content: string
  /** Pages Swifty was given, when not the whole document. */
  note?: string
  error?: boolean
  pending?: boolean
}

interface Props {
  sessionId: string
  filename: string
  onGoTo: (pageIndex: number) => void
  onClose: () => void
}

/** Conversations by session, so closing the panel does not lose one; a new
 * document is a new conversation. */
const conversations = new Map<string, Message[]>()

export function AskPanel({ sessionId, filename, onGoTo, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>(() => conversations.get(sessionId) ?? [])
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const abort = useRef<AbortController | null>(null)
  const list = useRef<HTMLDivElement>(null)

  // (A new document mounts a new panel -- see App -- so a new conversation.)
  useEffect(() => {
    conversations.set(sessionId, messages.filter((m) => !m.pending))
    if (conversations.size > 20) conversations.delete(conversations.keys().next().value!)
  }, [sessionId, messages])

  // Closing the panel mid-answer stops it.
  useEffect(() => () => abort.current?.abort(), [])

  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight })
  }, [messages])

  const send = async (text: string, summary = false) => {
    if (busy || (!summary && !text.trim())) return
    const history = messages.filter((m) => !m.error && !m.pending)
      .map((m) => ({ role: m.role, content: m.content }))
    const shown = summary ? 'Summarise this document.' : text.trim()
    setMessages((m) => [...m, { role: 'user', content: shown }, { role: 'assistant', content: '', pending: true }])
    setQuestion('')
    setBusy(true)
    const controller = new AbortController()
    abort.current = controller
    const update = (change: (last: Message) => Message) =>
      setMessages((m) => [...m.slice(0, -1), change(m[m.length - 1])])
    try {
      await api.ask(sessionId, { question: shown, summary, history }, (event) => {
        if (event.type === 'meta' && event.truncated) {
          update((last) => ({ ...last, note: `Read pages ${event.pages.join(', ')} of ${event.totalPages}, chosen for this question.` }))
        } else if (event.type === 'delta') {
          update((last) => ({ ...last, content: last.content + event.text }))
        } else if (event.type === 'error') {
          update((last) => ({ ...last, content: event.message, error: true, pending: false }))
        } else if (event.type === 'done') {
          update((last) => ({ ...last, pending: false }))
        }
      }, controller.signal)
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        update((last) => ({ ...last, content: err instanceof Error ? err.message : String(err), error: true, pending: false }))
      } else {
        // Stopped: say so, rather than leave an empty bubble or a sentence
        // that just ends.
        update((last) => last.content.trim()
          ? { ...last, content: last.content + ' \u2026', note: 'Stopped before Swifty finished.', pending: false }
          : { ...last, content: 'Stopped before Swifty answered.', error: true, pending: false })
      }
    } finally {
      update((last) => ({ ...last, pending: false }))
      setBusy(false)
    }
  }

  return (
    <aside className="comments ask" aria-label="Ask Swifty">
      <div className="comments__header">
        <span>Ask Swifty</span>
        <button className="iconbtn" title="Close" onClick={onClose}>{'✕'}</button>
      </div>
      <div className="ask__list" ref={list} aria-live="polite">
        {messages.length === 0 && (
          <div className="ask__intro">
            <p>Ask about <strong>{filename}</strong>: what it says, dates, amounts, who is responsible.</p>
            <button className="btn btn--small" onClick={() => void send('', true)}>Summarise this document</button>
            <p className="modal__hint">
              Swifty runs on the City&rsquo;s own servers and answers from this
              document only. Check anything important against the page it cites.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="ask__msg" data-role={m.role} data-error={m.error || undefined}>
            {m.note && <div className="ask__note">{m.note}</div>}
            {m.role === 'assistant' ? <Cited text={m.content} onGoTo={onGoTo} /> : m.content}
            {m.pending && <span className="ask__typing" aria-label="Swifty is writing">{m.content ? '' : 'Reading the document…'}</span>}
          </div>
        ))}
      </div>
      <form className="ask__form" onSubmit={(e) => { e.preventDefault(); void send(question) }}>
        <textarea
          value={question}
          rows={2}
          placeholder="Ask a question about this document"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(question) }
          }}
        />
        {busy
          ? <button type="button" className="btn btn--small" onClick={() => abort.current?.abort()}>Stop</button>
          : <button type="submit" className="btn btn--primary btn--small" disabled={!question.trim()}>Ask</button>}
      </form>
    </aside>
  )
}

/** Answer text with [p. N] citations turned into page links. */
function Cited({ text, onGoTo }: { text: string; onGoTo: (pageIndex: number) => void }) {
  const parts = text.split(/(\[(?:p|pp|page)\.?\s*\d+(?:\s*[-–,]\s*\d+)*\])/gi)
  return (
    <>
      {parts.map((part, i) => {
        const pages = /^\[(?:p|pp|page)/i.test(part) ? part.match(/\d+/g) : null
        if (!pages) return <span key={i}>{part}</span>
        return (
          <span key={i} className="ask__cite">
            {pages.map((p) => (
              <button key={p} className="linkbtn" onClick={() => onGoTo(Number(p) - 1)}>p.&nbsp;{p}</button>
            ))}
          </span>
        )
      })}
    </>
  )
}
